import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api, trackEvent } from "../lib/api";
import { useApp } from "../context/AppContext";
import { useSalesStatus } from "../components/PhysicalSalesBanner";
import { Star, Check, ZoomIn, X, ChevronLeft, ChevronRight, Shield, Truck, RotateCcw, Clock } from "lucide-react";
import ProductCard from "../components/ProductCard";
import Seo, { productJsonLd, breadcrumbJsonLd } from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import WishlistHeart from "../components/WishlistHeart";
import PeopleWatching from "../components/PeopleWatching";
import PurchaseCount from "../components/PurchaseCount";
import RecentlyViewed from "../components/RecentlyViewed";
import CrossSell from "../components/CrossSell";
import RelatedArticles from "../components/RelatedArticles";
import ProductReviews from "../components/ProductReviews";
import MarkdownRender from "../components/MarkdownRender";
import { trackView as trackRecentView } from "../lib/recentlyViewed";
import { tr, pick } from "../i18n/translations";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const { addToCart, lang, fmt } = useApp();
  const { should_block_physical, showBlockingModal } = useSalesStatus();

  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [zoomPos, setZoomPos] = useState({ x: 50, y: 50 });
  const [hovering, setHovering] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const imgWrapRef = useRef(null);
  const trackedRef = useRef(null);

  // Un article est "physique" si non-digital ET nécessite un envoi
  const isPhysicalItem = !product?.is_digital && !product?.no_shipping;
  const blockThisProduct = should_block_physical && isPhysicalItem;

  function handleAddToCart() {
    if (blockThisProduct) {
      showBlockingModal();
      return;
    }
    addToCart(product, qty);
    toast.success("Ajouté au panier");
  }

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      setActiveImg(0);
      if (trackedRef.current !== r.data.slug) {
        trackedRef.current = r.data.slug;
        trackEvent("view_product", { product_slug: r.data.slug, category_slug: r.data.category_slug });
        trackRecentView(r.data);
      }
    });
  }, [slug]);

  useEffect(() => {
    if (!lightbox) return;
    function onKey(e) {
      if (e.key === "Escape") setLightbox(false);
      if (e.key === "ArrowLeft") setActiveImg((i) => Math.max(0, i - 1));
      if (e.key === "ArrowRight") setActiveImg((i) => Math.min((product?.images?.length || 1) - 1, i + 1));
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, product]);

  if (!product) return <div className="p-20 text-center text-ink-soft">Chargement...</div>;

  function handleMove(e) {
    const rect = imgWrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomPos({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
  }

  const price = product.sale_price || product.price;
  const old = product.sale_price ? product.price : null;
  const imgs = product.images?.length ? product.images : ["https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=900"];
  const comp = product.competitor_prices || {};

  const productName = pick(product.name, lang);
  const productDesc = pick(product.description, lang) || `${productName} — matériel d'orthophonie sélectionné par des cliniciens. Livraison France & UE offerte dès 100€.`;
  const ldProduct = productJsonLd({
    name: productName,
    description: productDesc,
    image: imgs,
    price: product.sale_price || product.price,
    currency: product.currency || "EUR",
    slug: product.slug,
    rating: product.rating,
    reviewCount: product.rating_count,
    stock: product.stock,
    sku: product.sku || product.id || product.slug,
    category: product.category_slug,
  });
  const ldBreadcrumb = breadcrumbJsonLd([
    { name: "Accueil", path: "/" },
    { name: "Catalogue", path: "/catalogue" },
    ...(product.category_slug ? [{ name: product.category_slug, path: `/categorie/${product.category_slug}` }] : []),
    { name: productName, path: `/produit/${product.slug}` },
  ]);

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12">
      <Seo
        title={product.seo_title || `Jeu orthophonie · ${productName}`}
        description={product.seo_description || productDesc.slice(0, 160)}
        path={`/produit/${product.slug}`}
        image={imgs[0]}
        type="product"
        jsonLd={[ldProduct, ldBreadcrumb]}
        keywords={`${productName}, jeu orthophonie, ${product.category_slug}, matériel orthophonie${(product.troubles || []).length ? ', ' + product.troubles.join(', ') : ''}`}
      />
      <Breadcrumbs items={[
        { name: "Accueil", path: "/" },
        { name: "Catalogue", path: "/catalogue" },
        ...(product.category_slug ? [{ name: product.category_slug, path: `/categorie/${product.category_slug}` }] : []),
        { name: productName },
      ]} />
      <div className="mb-6" />

      <div className="grid lg:grid-cols-2 gap-12">
        <div>
          <div
            ref={imgWrapRef}
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => setHovering(false)}
            onMouseMove={handleMove}
            onClick={() => setLightbox(true)}
            className="relative aspect-square rounded-3xl overflow-hidden bg-ivory-200 mb-4 cursor-zoom-in select-none group"
            data-testid="product-main-image"
          >
            <img
              src={imgs[activeImg]}
              alt={pick(product.name, lang)}
              fetchpriority="high"
              decoding="async"
              className="w-full h-full object-cover transition-transform duration-200 ease-out"
              style={{
                transform: hovering ? "scale(2.2)" : "scale(1)",
                transformOrigin: `${zoomPos.x}% ${zoomPos.y}%`,
              }}
              draggable={false}
            />
            <div className="absolute top-3 end-3 bg-white/85 backdrop-blur-sm rounded-full p-2 text-ink opacity-0 group-hover:opacity-100 transition" data-testid="product-zoom-hint">
              <ZoomIn size={16} />
            </div>
          </div>
          {imgs.length > 1 && (
            <div className="flex gap-3 flex-wrap">
              {imgs.map((src, i) => (
                <button
                  key={i}
                  data-testid={`product-thumb-${i}`}
                  onClick={() => setActiveImg(i)}
                  className={`w-20 h-20 rounded-2xl overflow-hidden border-2 transition ${activeImg === i ? "border-pedablue" : "border-transparent hover:border-ink/20"}`}
                >
                  <img src={src} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="flex gap-2 mb-3 flex-wrap">
            {old && <span className="sticker-coral" data-testid="product-discount-badge">-{Math.round((1 - price / old) * 100)}%</span>}
            {product.badges?.new && <span className="sticker-sage">Nouveau</span>}
            {product.badges?.top && <span className="sticker-ink">Top</span>}
            {product.badges?.featured && <span className="sticker-ink">Recommandé orthos</span>}
            {product.badges?.deal && <span className="bg-coral text-ivory px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest">🔥 Offre du moment</span>}
          </div>
          <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-light tracking-tight" data-testid="product-title">{pick(product.name, lang)}</h1>

          <div className="flex items-center gap-2 mt-4">
            {Array.from({ length: 5 }).map((_, i) => <Star key={i} size={14} className="fill-coral text-coral" />)}
            <span className="text-sm">{product.rating || 4.9}</span>
            <span className="text-xs text-ink-soft">({product.rating_count || product.reviews?.length || 0} avis)</span>
          </div>

          <div className="mt-6 flex items-end gap-3 flex-wrap" data-testid="product-pricing">
            <p className="font-display text-5xl text-pedablue" data-testid="product-price">{fmt(price)}</p>
            {old && (
              <>
                <p className="text-ink-soft line-through mb-2 text-lg" data-testid="product-price-old">{fmt(old)}</p>
                <span className="bg-coral/15 text-coral-dark px-2 py-1 rounded-full text-xs font-semibold mb-2" data-testid="product-savings">
                  Économie {fmt(old - price)}
                </span>
              </>
            )}
            <p className="text-xs text-ink-soft mb-2 w-full">{tr(lang, "price_ht")} · {tr(lang, "incl_vat")}</p>
          </div>

          <p className="mt-6 text-ink-soft leading-relaxed text-base">{pick(product.description, lang)}</p>
          {pick(product.long_description, lang) && (
            <div className="mt-6 text-ink-soft leading-relaxed text-sm article-content [&_h1]:font-display [&_h1]:text-2xl [&_h1]:mt-6 [&_h1]:mb-3 [&_h1]:text-ink [&_h2]:font-display [&_h2]:text-xl [&_h2]:mt-6 [&_h2]:mb-2 [&_h2]:text-pedablue [&_h3]:font-semibold [&_h3]:text-base [&_h3]:mt-4 [&_h3]:mb-1 [&_h3]:text-ink [&_p]:mb-3 [&_strong]:font-semibold [&_strong]:text-ink [&_em]:italic [&_a]:text-pedablue [&_a]:underline">
              <MarkdownRender md={pick(product.long_description, lang)} />
            </div>
          )}

          <div className="mt-8 flex items-center gap-3">
            <div className="flex items-center bg-ivory-200 rounded-full">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} disabled={(product.stock ?? 100) <= 0} className="px-4 py-2.5 disabled:opacity-40" data-testid="qty-minus">−</button>
              <span className="px-2 text-sm font-medium" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} disabled={(product.stock ?? 100) <= 0} className="px-4 py-2.5 disabled:opacity-40" data-testid="qty-plus">+</button>
            </div>
            {(product.stock ?? 100) <= 0 ? (
              <div className="flex-1" data-testid="product-out-of-stock">
                <button disabled className="btn-primary opacity-40 cursor-not-allowed w-full">
                  Rupture de stock
                </button>
                <p className="text-xs text-coral-dark mt-2">
                  Ce produit est temporairement indisponible. <a href="/contact" className="underline">Recevoir une alerte de réapprovisionnement</a>
                </p>
              </div>
            ) : (
              <button
                data-testid={blockThisProduct ? "product-add-to-cart-blocked" : "product-add-to-cart"}
                onClick={handleAddToCart}
                className={`btn-primary ${blockThisProduct ? "bg-amber-500 hover:bg-amber-600" : ""}`}
                title={blockThisProduct ? "Vente physique temporairement suspendue en France et UE — cliquez pour en savoir plus" : ""}
              >
                {blockThisProduct ? "⚠ En savoir plus" : tr(lang, "add_to_cart")}
              </button>
            )}
            <WishlistHeart productId={product.id} size={24} className="bg-ivory-200 hover:bg-ivory-300" />
          </div>
          <div className="mt-3 min-h-[1.5rem] flex flex-col gap-1">
            <PeopleWatching slug={product.slug} />
            <PurchaseCount slug={product.slug} />
          </div>
          <p className="text-xs text-ink-soft mt-1 italic">{tr(lang, "shipping_notice")}</p>

          {/* Trust badges C3 — sécurité / livraison / garantie */}
          <div className="mt-6 grid grid-cols-2 gap-3" data-testid="trust-badges">
            <div className="flex items-start gap-2.5 bg-ivory-200/40 rounded-2xl p-3">
              <Shield size={18} className="text-pedablue mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold">Paiement 100% sécurisé</p>
                <p className="text-[10px] text-ink-soft">via PayPal — chiffrement SSL</p>
              </div>
            </div>
            <div className="flex items-start gap-2.5 bg-ivory-200/40 rounded-2xl p-3">
              <RotateCcw size={18} className="text-pedablue mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold">Satisfait ou remboursé</p>
                <p className="text-[10px] text-ink-soft">Droit de rétractation 14 jours</p>
              </div>
            </div>
            <div className="flex items-start gap-2.5 bg-ivory-200/40 rounded-2xl p-3">
              <Truck size={18} className="text-pedablue mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold">Livraison offerte</p>
                <p className="text-[10px] text-ink-soft">dès 100 € (Europe) · 150 € (Afrique) · 200 € (monde)</p>
              </div>
            </div>
            <div className="flex items-start gap-2.5 bg-ivory-200/40 rounded-2xl p-3">
              <Clock size={18} className="text-pedablue mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold">Traitement rapide</p>
                <p className="text-[10px] text-ink-soft">expédition par nos fournisseurs partenaires</p>
              </div>
            </div>
          </div>

          {product.details && (
            <div className="mt-10 grid sm:grid-cols-2 gap-4">
              {Object.entries(product.details).map(([k, v]) => {
                // Hide "approche" detail (user requested removal of the concept site-wide)
                if (k.toLowerCase().includes("approche")) return null;
                const txt = pick(v, lang);
                if (!txt) return null;
                return (
                  <div key={k} className="bg-ivory-200 rounded-2xl p-5">
                    <p className="text-xs uppercase tracking-widest text-ink/60 mb-2">{k.replace("_", " ")}</p>
                    <p className="text-sm">{txt}</p>
                  </div>
                );
              })}
            </div>
          )}

          {Object.keys(comp).length > 0 && false && (
            <div className="mt-10 bg-sage/20 rounded-3xl p-6" data-testid="competitor-compare">
              <p className="font-display text-xl mb-1">{tr(lang, "competitor_compare")}</p>
              <p className="text-xs text-ink-soft mb-4">{tr(lang, "lower_price")}</p>
              <div className="space-y-2">
                <Row label="Orthophonie Online" price={price} highlight />
                {comp.hop_toys && <Row label="Hop'Toys" price={comp.hop_toys} />}
                {comp.cit_inspir && <Row label="Cit'Inspir" price={comp.cit_inspir} />}
                {comp.amazon && <Row label="Amazon" price={comp.amazon} />}
                {comp.pirouette && <Row label="Pirouette" price={comp.pirouette} />}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ═══ CLINICAL SECTIONS — Phase E ══════════════════════════════════ */}
      {(product.troubles?.length > 0 || product.category_slug || product.ages?.length > 0) && (
        <div className="mt-14 grid md:grid-cols-2 gap-5" data-testid="product-clinical-sections">
          {/* Ce que ce matériel permet de travailler */}
          {(product.troubles?.length > 0 || product.category_slug) && (
            <section className="rounded-3xl bg-white border border-ink/10 p-6 md:p-7" data-testid="product-section-objectifs">
              <div className="flex items-start gap-3 mb-4">
                <span className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-sage/15 text-sage-dark">
                  <Check size={18} />
                </span>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-ink-soft">Objectifs cliniques</p>
                  <h2 className="font-display text-xl md:text-2xl mt-0.5 leading-tight">Ce que ce matériel permet de travailler</h2>
                </div>
              </div>
              {product.category_slug && (
                <div className="mb-3">
                  <p className="text-[11px] uppercase tracking-widest text-ink-soft mb-1.5">Catégorie</p>
                  <span className="inline-flex items-center rounded-full px-3 py-1 text-xs bg-sage/15 text-sage-dark border border-sage/25 capitalize">
                    {product.category_slug.replace(/-/g, " ")}
                  </span>
                </div>
              )}
              {product.troubles?.length > 0 && (
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-ink-soft mb-1.5">Troubles ciblés</p>
                  <div className="flex flex-wrap gap-1.5">
                    {product.troubles.map((t) => (
                      <span key={t} className="inline-flex items-center rounded-full px-3 py-1 text-xs bg-sage/15 text-sage-dark border border-sage/25">{t}</span>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}
          {/* Pour qui ? */}
          {(product.troubles?.length > 0 || product.ages?.length > 0 || product.age_range) && (
            <section className="rounded-3xl bg-white border border-ink/10 p-6 md:p-7" data-testid="product-section-public">
              <div className="flex items-start gap-3 mb-4">
                <span className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-pedablue/10 text-pedablue-dark">
                  <Check size={18} />
                </span>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-ink-soft">Indications</p>
                  <h2 className="font-display text-xl md:text-2xl mt-0.5 leading-tight">Pour qui ?</h2>
                </div>
              </div>
              {(product.age_range || (product.ages && product.ages.length > 0)) && (
                <div className="mb-3">
                  <p className="text-[11px] uppercase tracking-widest text-ink-soft mb-1.5">Tranches d&apos;âge</p>
                  <div className="flex flex-wrap gap-1.5">
                    {product.age_range && (
                      <span className="inline-flex items-center rounded-full px-3 py-1 text-xs bg-coral/10 text-coral-dark border border-coral/25">{product.age_range}</span>
                    )}
                    {(product.ages || []).map((a) => (
                      <span key={a} className="inline-flex items-center rounded-full px-3 py-1 text-xs bg-coral/10 text-coral-dark border border-coral/25">{a} ans</span>
                    ))}
                  </div>
                </div>
              )}
              {product.troubles?.length > 0 && (
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-ink-soft mb-1.5">Profils cliniques</p>
                  <div className="flex flex-wrap gap-1.5">
                    {product.troubles.map((t) => (
                      <span key={t} className="inline-flex items-center rounded-full px-3 py-1 text-xs bg-pedablue/10 text-pedablue-dark border border-pedablue/25">{t}</span>
                    ))}
                  </div>
                </div>
              )}
              <p className="text-xs text-ink-soft mt-4 italic">
                Utilisable en cabinet d&apos;orthophonie, en classe spécialisée ou à domicile — sous encadrement.
              </p>
            </section>
          )}
        </div>
      )}

      {product.reviews?.length > 0 && (
        <section className="mt-20" data-testid="product-reviews-static">
          <h2 className="font-display text-3xl mb-8">{tr(lang, "reviews_title")}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {product.reviews.map((r, i) => (
              <div key={i} className="bg-white border border-ink/10 rounded-3xl p-6">
                <div className="flex items-center gap-1 mb-2">
                  {Array.from({ length: r.rating || 5 }).map((_, k) => <Star key={k} size={12} className="fill-coral text-coral" />)}
                </div>
                <p className="text-sm leading-relaxed">« {r.text} »</p>
                <p className="text-xs text-ink-soft mt-3"><Check size={12} className="inline text-sage-dark me-1" />{r.author} · {r.role}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Real product reviews (user-submitted, moderated) */}
      <ProductReviews slug={product.slug} />

      {/* Cross-sell — "Souvent acheté avec" */}
      <CrossSell slug={product.slug} />

      {/* Related blog articles — SEO internal linking */}
      <RelatedArticles productSlug={product.slug} />

      {/* Recently viewed (localStorage) */}
      <RecentlyViewed excludeSlug={product.slug} />

      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-ink/95 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setLightbox(false)}
          data-testid="product-lightbox"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightbox(false); }}
            className="absolute top-6 end-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-ivory flex items-center justify-center transition"
            data-testid="lightbox-close"
            aria-label="Fermer"
          >
            <X size={22} />
          </button>
          {imgs.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setActiveImg((i) => Math.max(0, i - 1)); }}
                disabled={activeImg === 0}
                className="absolute start-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-ivory flex items-center justify-center transition disabled:opacity-20"
                data-testid="lightbox-prev"
                aria-label="Précédent"
              >
                <ChevronLeft size={22} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setActiveImg((i) => Math.min(imgs.length - 1, i + 1)); }}
                disabled={activeImg === imgs.length - 1}
                className="absolute end-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-ivory flex items-center justify-center transition disabled:opacity-20"
                data-testid="lightbox-next"
                aria-label="Suivant"
              >
                <ChevronRight size={22} />
              </button>
            </>
          )}
          <img
            src={imgs[activeImg]}
            alt={pick(product.name, lang)}
            loading="lazy"
            decoding="async"
            className="max-w-[90vw] max-h-[88vh] object-contain rounded-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="lightbox-image"
          />
          {imgs.length > 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 bg-white/10 backdrop-blur-md rounded-full px-3 py-2">
              {imgs.map((_, i) => (
                <button
                  key={i}
                  onClick={(e) => { e.stopPropagation(); setActiveImg(i); }}
                  className={`w-2 h-2 rounded-full transition ${activeImg === i ? "bg-ivory w-6" : "bg-ivory/40 hover:bg-ivory/70"}`}
                  aria-label={`Image ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ label, price, highlight }) {
  const { fmt } = useApp();
  return (
    <div className={`flex items-center justify-between py-2 ${highlight ? "font-display text-lg" : "text-sm text-ink-soft"}`}>
      <span>{label}</span>
      <span>{fmt(price)}</span>
    </div>
  );
}
